// src/lib/api.js
// Authenticated fetch wrapper — the single choke point for every /api call.
//
// Attaches the current user's Supabase access token so the backend resolves the
// tenant from the JWT. As of Phase 4 every /api call in src/ goes through here;
// a test in api/__tests__/frontend-jwt.test.js fails the build if a raw
// fetch('/api/...') is reintroduced.

import { supabase } from './supabaseClient';

// ── Session-expiry handling (B14) ────────────────────────────────────────────
//
// Before the Phase 5 cutover nothing 401s: a request with no valid token falls
// back to the legacy service-role context and returns data. After the cutover it
// will, and the failure is quiet — components do `.then(r => r.json())` without
// checking status, so an expired session renders an empty page rather than
// saying anything. Empty is also what a correctly-working tenant with no data
// looks like, which makes the two indistinguishable at a glance.
//
// apiFetch cannot simply throw on 401: ~260 call sites expect a Response object
// (`res.ok`, `.then(r => r.json())`). Changing that contract would mean editing
// every one of them. So the 401 is reported through a side channel and the
// Response is still returned — callers keep working unchanged, and one listener
// at the app root can react.
//
// AuthContext subscribes to this and signs the user out, which sends them to the
// login page instead of a blank screen.

const listeners = new Set();

/** Subscribe to auth failures. Returns an unsubscribe function. */
export function onAuthFailure(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyAuthFailure(path, status) {
  // Logged unconditionally: during the Phase 5 cutover this is the fastest signal
  // that a specific endpoint is rejecting a token the frontend believes is valid.
  console.warn(`[apiFetch] ${status} from ${path} — session is not valid for this request`);
  for (const fn of listeners) {
    try { fn({ path, status }); } catch { /* a bad listener must not break the fetch */ }
  }
}

export async function apiFetch(path, options = {}) {
  let { data } = await supabase.auth.getSession();
  let token = data?.session?.access_token;

  // A missing token here is not normal. AuthGate blocks rendering until the
  // session resolves, so a component calling this should always have one. But
  // getSession() can transiently return null — mid token-refresh, or while a
  // stored session is being re-validated — and the original code silently sent
  // the request WITHOUT an Authorization header when that happened.
  //
  // That was invisible before the migration, because the backend's legacy
  // fallback answered anyway with service-role data. The [AUTH-LEGACY] log
  // caught exactly one such request on 21 Aug: a real Firefox call to
  // /api/accounts/list from the app, carrying no token. After the Phase 5
  // cutover the same request would 401 and the page would render empty.
  //
  // So: try once to refresh rather than proceeding tokenless. If that fails the
  // request still goes out — degrading to the old behaviour is better than
  // throwing — but it is logged, so a recurring race is visible rather than
  // silently absorbed.
  if (!token) {
    const refreshed = await supabase.auth.refreshSession().catch(() => null);
    token = refreshed?.data?.session?.access_token;
    if (!token) {
      console.warn(`[apiFetch] no access token for ${path} — sending unauthenticated. ` +
                   'Before the Phase 5 cutover the backend answers via the legacy path; ' +
                   'after it, this request will 401.');
    }
  }

  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const res = await fetch(path, { ...options, headers });

  // 401 = no/invalid token. 403 = authenticated but not permitted, which is not a
  // session problem and must NOT sign the user out.
  if (res.status === 401) notifyAuthFailure(path, res.status);

  return res;
}

/**
 * Start a provider OAuth connection (Microsoft today; Zoho would be identical).
 *
 * WHY THIS EXISTS RATHER THAN AN <a href>
 * The connect buttons used to be plain links to /api/auth/microsoft/login. A link is a
 * top-level browser navigation: it carries cookies and nothing else. Our Supabase
 * session lives in localStorage (supabaseClient.js, storageKey 'digitalme_auth'), so the
 * request arrived with no Authorization header, getRequestContext found no tenant, and
 * the endpoint answered "Sign in before connecting Microsoft 365" — to a signed-in user,
 * every time. The button could never have worked.
 *
 * So: ask for the authorize URL over an authenticated fetch, then navigate to it. The
 * endpoint also sets the signed OAuth-state cookie on this response, which the callback
 * later matches — so this must be a real request to our own origin, not a URL we build
 * in the browser.
 *
 * Returns an error string on failure; on success the browser navigates away and nothing
 * after the assignment runs.
 */
export async function startOAuthConnect(provider = 'microsoft') {
  const res = await apiFetch(`/api/auth/${provider}/login?format=json`);
  let body = null;
  try { body = await res.json(); } catch { /* fall through to the status check */ }

  if (!res.ok || !body?.authUrl) {
    return body?.error || `Could not start the ${provider} connection (HTTP ${res.status})`;
  }
  window.location.assign(body.authUrl);
  return null;
}
